import Vue from "vue";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import {
  required,
  alpha,
  between,
  email,
  min_value,
  max_value,
  alpha_spaces,
  integer,
  max,
} from "vee-validate/dist/rules";
import moment from "moment";
import { phoneValidationRule, validatePhoneNumberBasic } from "@/utils/phoneValidation";
// const emailRegex = new RegExp(
//   "([a-zA-Z0-9._%+\\p{L}-]+@[a-zA-Z0-9-\\p{L}]+\\.[a-zA-Z]{2,}$)",
//   "gm"
// );
const emailRegex = /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// const nameRegex = /^[\p{L}\p{N}\p{P}\p{S}\s]+$/imu;
const nameRegex = /^[\p{L}\p{N}\p{P}\p{S}\p{Z}\p{M}\p{C}]+$/imu;
const emailValidator = {
  getMessage(field, args) {
    // will be added to default locale messages.
    // Returns a message.
    return "Not a valid email address";
  },
  validate(value, args) {
    // Returns a Boolean or a Promise that resolves to a boolean.
    return emailRegex.test(value);
  },
};
const nameValidator = {
  getMessage(field, args) {
    // will be added to default locale messages.
    // Returns a message.
    return "Not a valid name address";
  },
  validate(value, args) {
    // Returns a Boolean or a Promise that resolves to a boolean.
    return nameRegex.test(value);
  },
};
// Enhanced phone number validation rules
extend("phone", {
  validate: (value: string): boolean | string => {
    if (!value) return true; // Allow empty values (use required rule separately)
    return phoneValidationRule(value);
  },
  message: "Please enter a valid phone number",
});

extend("phone_us", {
  validate: (value: string): boolean | string => {
    if (!value) return true;
    const result = validatePhoneNumberBasic(value);
    if (!result.valid) {
      return result.message || "Please enter a valid US phone number";
    }
    // Additional US-specific validation
    const cleaned = value.replace(/\D/g, '');
    const digits = cleaned.replace(/^1/, ''); // Remove country code if present
    if (digits.length !== 10) {
      return "US phone numbers must be 10 digits";
    }
    return true;
  },
});

extend("phone_international", {
  validate: (value: string): boolean | string => {
    if (!value) return true;
    if (!value.startsWith('+')) {
      return "International phone numbers must start with country code (+)";
    }
    const result = validatePhoneNumberBasic(value);
    return result.valid || result.message || "Please enter a valid international phone number";
  },
});

// Legacy rule for backward compatibility - now using enhanced validation
extend("phoneNumberLength", {
  params: ["phoneLength"],
  validate: (v: any, { phoneLength }: any) => {
    const result = validatePhoneNumberBasic(v);
    return result.valid;
  },
  message: "Please enter a valid phone number",
});

extend("min_value", {
  ...min_value,
  message: (_, { min, _field_ }) => `${_field_} must be greater than ${min}`,
});

extend("max_value", {
  ...max_value,
  message: (_, { max, _field_ }) => `${_field_} must be less than ${max}`,
});

extend("max", {
  ...max,
  message: (_, { _field_, length }) =>
    `${_field_} cannot be longer than ${length} characters`,
});

extend("c_email", emailValidator);
extend("email", {
  ...email,
  message: "Not a valid email address",
});
extend("between", {
  ...between,
  message: "Must be a year",
});

extend("required", {
  ...required,
  message: (_, { _field_, length }) => `${_field_} is required`,
});

extend("checkbox", {
  validate: (v) => !!v,
  message: "This is required",
});

extend("password_len", {
  validate: (v) => {
    // Check minimum length of 8 characters
    if (v.length < 8) {
      return false;
    }
    
    // Check for at least one symbol
    const symbolRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (!symbolRegex.test(v)) {
      return false;
    }
    
    return true;
  },
  message: "Must be at least 8 characters and contain at least one symbol (!@#$%^&*(),.?\":{}|<>)",
});

extend("password", {
  params: ["target"],
  // @ts-ignore
  validate(value, { target }) {
    return value === target;
  },
  message: "Password must be the same",
});

extend("c_name", nameValidator);
extend("name", {
  ...alpha,
  message: "Alphabets only",
});

extend("name_with_spaces", {
  ...alpha_spaces,
  message: "Alphabets only",
});

extend("name_with_specific_symbols", {
  params: ["numbers_allowed"],
  validate: (v: string, { numbers_allowed }: Record<string, any>) => {
    if (Boolean(numbers_allowed)) {
      return /^[0-9A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸÁÍÑÓÚ \-—."`]*$/i.test(v);
    }

    return /^[A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸÁÍÑÓÚ \-—."`]*$/i.test(v);
  },
  message: "{_field_} contains invalid symbols",
});

extend("name_with_all_symbols", {
  params: ["numbers_allowed"],
  validate: (v, { numbers_allowed }: Record<string, any>) => {
    const allowedSymbols = '0-9A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸÁÍÑÓÚ \\-—."`$@#&*()©®.'; // Include additional symbols here

    if (Boolean(numbers_allowed)) {
      return new RegExp(`^[${allowedSymbols}]*$`, "i").test(v);
    }

    return new RegExp(
      `^[A-ZÀÂÆÇÉÈÊËÏÎÔŒÙÛÜŸÁÍÑÓÚ \\-—.\"\`${allowedSymbols}]*$`,
      "i"
    ).test(v);
  },
  message: "{_field_} contains invalid symbols",
});

extend("code", {
  validate: (v) => v.length <= 6,
  message: "Not a valid code",
});

extend("decimal", {
  validate(value) {
    const regex = /^\d*(\.\d{1,2})?$/;
    return value.toString().match(regex);
  },
  message: "Not a valid number",
});

extend("demoreel", {
  validate: (v) =>
    /^(https?:\/\/)?((www\.)?youtube\.com|youtu\.?be)\/.+$/.test(v) ||
    /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/.test(
      v
    ),
  message: "URL must be either Youtube or Vimeo ",
});

let urlErrorMessage = "";
extend("url", {
  validate: (v: string) => {
    const URLPrefixRegex = /^(?:http(s)?:\/\/)/;
    // More lenient regex that accepts:
    // - localhost with or without port
    // - IP addresses (e.g., 192.168.1.1:8080)
    // - Standard domains with TLD
    // - Domains with subdomains
    const URLRegex =
      /^(?:http(s)?:\/\/)(?:[\w.-]+(?:\.[\w\.-]+)*|localhost|(?:\d{1,3}\.){3}\d{1,3})(?::\d+)?(?:[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.%]*)?$/;

    if (!URLPrefixRegex.test(v.trim())) {
      urlErrorMessage = 'Not a valid URL (URL should start with "http(s)://")';
      return false;
    }

    if (!URLRegex.test(v.trim())) {
      urlErrorMessage = "Not a valid URL";
      return false;
    }

    return true;
  },
  message: () => urlErrorMessage,
});

extend("noAfter", {
  params: ["dateToCompare", "description"],
  validate: (chosenDate: string | Date, { dateToCompare }: any): boolean => {
    return isFirstDateLargerOrEqual(dateToCompare, chosenDate);
  },
  message: (_, { description, dateToCompare }): string => {
    if (description) {
      const date = getDateFormatted(dateToCompare);
      return `This date cannot be later than ${description} - ${date}`;
    }
    return `This date cannot be later than ${dateToCompare}`;
  },
});

extend("noBeforeDate", {
  params: ["target", "ongoing"],
  validate: (v: any, { target, ongoing }: any) => {
    if (!target || ongoing) {
      return true;
    }
    const newDate = new Date(v);
    const targetDate = target ? new Date(target) : newDate;

    return newDate > targetDate;
  },
  message: "{_field_} cannot go before {target}",
});

extend("noPastDate", {
  validate: (val: Date | string) => {
    const today = new Date().setHours(0, 0, 0, 0);
    const target = new Date(val).getTime();

    return target >= today;
  },
  message: "{_field_} cannot be in the past",
});

extend("noFutureDate", {
  params: ["ongoing"],
  validate: (val: Date | string, { ongoing }: any) => {
    if (!ongoing) {
      return true;
    }
    const today = new Date().setHours(0, 0, 0, 0);
    const target = new Date(val).getTime();

    return target <= today;
  },
  message: "{_field_} cannot be in the future",
});

extend("noBefore", {
  params: ["dateToCompare", "description"],
  validate: (chosenDate: string | Date, { dateToCompare }: any): boolean => {
    return isFirstDateLargerOrEqual(chosenDate, dateToCompare);
  },
  message: (_, { description, dateToCompare }): string => {
    if (description) {
      const date = getDateFormatted(dateToCompare);
      return `This date cannot be earlier than ${description} - ${date}`;
    }
    return `This date cannot be earlier than ${dateToCompare}`;
  },
});

function isFirstDateLargerOrEqual(
  first: string | Date,
  second: string | Date
): boolean {
  const firstDate = moment(first).format("YYYY-MM-DD");
  const secondDate = moment(second).format("YYYY-MM-DD");
  return firstDate >= secondDate;
}

function getDateFormatted(date: string): string {
  return moment(date).format("DD MMM YYYY");
}

extend("noBigger", {
  params: ["target"],
  validate: (v: any, { target }: any) => {
    if (!target) {
      return true;
    }
    return parseInt(v) >= parseInt(target);
  },
  message: "{_field_} cannot be smaller than {target}",
});
extend("noSmaller", {
  params: ["target"],
  validate: (v: any, { target }: any) => {
    if (!target) {
      return true;
    }
    return parseInt(v) <= parseInt(target);
  },
  message: "{_field_} cannot be greater than {target}",
});

extend("required_field", {
  ...required,
  message: `{_field_} is required`,
});

extend("integer", {
  ...integer,
  message: `{_field_} should be an integer`,
});

extend("project_required", {
  params: ["project"],
  validate: (_, params: Record<string, any>): boolean => {
    return Boolean(params.project);
  },
  message: "You have to choose a project firstly",
});

// Register it globally
Vue.component("ValidationProvider", ValidationProvider);
Vue.component("ValidationObserver", ValidationObserver);
